import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { FeatureKey } from '@lynx-crypto/contracts/feature-flags';
import { SidebarControls, SidebarMenu, type SidebarNavItem, type SidebarNavGroup } from './SidebarSections';
import { useSettingsStore } from '../stores/settingsStore';
import { useAuthStore } from '../stores/authStore';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { navItemPassesFeatureGate } from './navGating';

// Maps a nav item's route to the feature flag that governs it. Items without a
// mapping (e.g. System/Admin) are never feature-flagged — they're role-gated.
const NAV_FEATURE_BY_PATH: Record<string, FeatureKey> = {
  '/': 'dashboard',
  '/chart': 'chart',
  '/sentiment': 'sentiment',
  '/trading': 'trading',
  '/automated': 'automatedTrading',
  '/journal': 'journal',
  '/wallet-compare': 'walletCompare',
  '/strategy': 'strategyBuilder',
  '/research': 'hunt',
  '/backtest': 'backtest',
};

const MARKETS_GROUP: SidebarNavGroup = {
  label: 'Markets',
  items: [
    { path: '/', label: 'Dashboard', icon: 'grid' },
    { path: '/chart', label: 'Chart', icon: 'candlestick' },
    { path: '/sentiment', label: 'Sentiment', icon: 'trending' },
  ],
};

const TRADING_GROUP: SidebarNavGroup = {
  label: 'Trading',
  items: [
    { path: '/trading', label: 'Portfolios', icon: 'wallet' },
    { path: '/automated', label: 'Automated Trading', icon: 'crosshair' },
    { path: '/journal', label: 'Trade Journal', icon: 'activity' },
    { path: '/wallet-compare', label: 'Compare Wallets', icon: 'trending' },
  ],
};

// Strategy authoring + validation: the composable Builder, the conversational
// Hunt (Hunt / Saved Iterations / Registered library), and Backtest for
// evaluating a strategy before it touches a wallet.
const STRATEGIES_GROUP: SidebarNavGroup = {
  label: 'Strategies',
  items: [
    { path: '/strategy', label: 'Builder', icon: 'crosshair' },
    { path: '/research', label: 'Hunt', icon: 'search' },
    { path: '/backtest', label: 'Backtest', icon: 'backtest' },
  ],
};

const ADMIN_GROUP: SidebarNavGroup = {
  label: 'Admin',
  items: [
    { path: '/system', label: 'System', icon: 'monitor' },
    { path: '/gate', label: 'Data Gate', icon: 'shield' },
    { path: '/admin', label: 'Admin', icon: 'shield' },
  ],
};

// Routes that are spot-only — rendered dim + non-clickable when a
// non-spot market is selected. Spans the Trading and Strategies groups.
const TRADING_ITEMS_SPOT_ONLY = new Set<string>([
  '/trading',
  '/automated',
  '/journal',
  '/wallet-compare',
  '/backtest',
  '/strategy',
]);

export function Sidebar() {
  const navigate = useNavigate();
  const collapsed = useSettingsStore((s) => s.sidebarCollapsed);
  const selectedMarket = useSettingsStore((s) => s.selectedMarket);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const { canUse, loaded: flagsLoaded } = useFeatureAccess();

  const groups = useMemo(() => {
    const lock = (group: SidebarNavGroup): SidebarNavGroup => ({
      ...group,
      items: group.items.map((item) => ({ ...item, locked: true })),
    });

    // When the user selects a non-spot market, mark trading items that
    // don't support that market as `disabled`. The item renders dim, a
    // tooltip explains why, and the click is a noop — preventing the
    // user from landing on a page that's just a LockedState.
    const marketDisable = (group: SidebarNavGroup): SidebarNavGroup => {
      if (selectedMarket === 'spot') return group;
      const reason = `${selectedMarket === 'margin' ? 'Margin' : 'Futures'} trading isn't supported yet — switch to Spot to use this.`;
      return {
        ...group,
        items: group.items.map((item) =>
          TRADING_ITEMS_SPOT_ONLY.has(item.path)
            ? { ...item, disabled: true, disabledReason: reason }
            : item,
        ),
      };
    };

    const g: SidebarNavGroup[] = [MARKETS_GROUP];
    g.push(
      isAuthenticated
        ? marketDisable(TRADING_GROUP)
        : lock(TRADING_GROUP),
    );
    // Strategies: Builder is auth+spot-gated; Hunt (/research) has its own
    // tier guard, so it stays reachable for signed-in users (the page shows
    // an in-place access panel when the tier is missing) and is padlocked
    // only when signed out.
    g.push(
      isAuthenticated
        ? marketDisable(STRATEGIES_GROUP)
        : lock(STRATEGIES_GROUP),
    );
    // Admin group is ADMIN-ONLY: hidden entirely for non-admins (including
    // signed-out visitors). Unlike Trading/Strategies — which padlock when
    // signed out so a visitor knows to sign in — Admin is an operator surface
    // that ordinary users can never reach, so advertising it with a padlock is
    // noise. Show it only when the user actually is an admin.
    if (role === 'admin') g.push(ADMIN_GROUP);

    // FEATURE-FLAG FILTER: once flags load, REMOVE any nav item whose feature is
    // disabled or scoped to a tier this user doesn't meet (shared resolver). A
    // disabled feature disappears from the nav entirely — not padlocked (padlock
    // means "sign in"). Groups left empty are dropped. Until flags load we leave
    // the nav as-is to avoid a flicker; the route + backend still enforce.
    //
    // A `locked` item ALWAYS survives this filter (see navItemPassesFeatureGate):
    // lock means "you're signed out, sign in to use this", which takes precedence
    // over the feature gate. Without it, every `public`-tier item would be dropped
    // for signed-out visitors — they'd vanish instead of showing a padlock.
    return g
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          navItemPassesFeatureGate(item, NAV_FEATURE_BY_PATH, flagsLoaded, canUse),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [isAuthenticated, role, selectedMarket, canUse, flagsLoaded]);

  return (
    <aside
      className={`
        flex flex-col h-full
        bg-sidebar-bg border-r border-sidebar-border
        transition-all duration-200
        ${collapsed ? 'w-16' : 'w-56'}
      `}
    >
      <SidebarMenu groups={groups} collapsed={collapsed} onNavigate={navigate} />
      <SidebarControls collapsed={collapsed} />
    </aside>
  );
}
